// The "↻ N" pill for Drive-sync activity from the most recent sync, on the Operations
// dashboard's rows. Hidden when sync is disabled or the last sync did nothing, except the
// reconnect-needed state, which always shows.
import { Link } from "react-router-dom";
import type { DriveSyncRecent } from "../api/hooks/useOperationsDashboard";

interface Props {
  projectId: string;
  recent: DriveSyncRecent | null | undefined;
  compact?: boolean;
}

type Tone = "green" | "red";

const TONE_STYLE: Record<Tone, { background: string; color: string; border: string }> = {
  green: { background: "#e8f5e8", color: "#2d5d2d", border: "1px solid #b3d9b3" },
  red: { background: "transparent", color: "#b03030", border: "1px solid #b03030" },
};

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const min = Math.round(diffMs / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const h = Math.round(min / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.round(h / 24);
    return `${d}d ago`;
  } catch {
    return iso;
  }
}

export const SyncActivityBadge = ({ projectId, recent, compact }: Props) => {
  if (!recent || !recent.enabled) return null;
  const total = recent.added + recent.changed + recent.removed;
  const isAuthFailed = recent.status === "auth_failed";
  if (total === 0 && !isAuthFailed) return null;

  let tone: Tone;
  let label: string;
  let title: string;
  let to: string;
  if (isAuthFailed) {
    tone = "red";
    label = "⚠ Drive paused";
    title = "Drive sync paused - authorization expired. Reconnect from the project's settings.";
    to = `/projects/${encodeURIComponent(projectId)}/settings`;
  } else {
    const parts: string[] = [];
    if (recent.added > 0) parts.push(`${recent.added} new`);
    if (recent.changed > 0) parts.push(`${recent.changed} updated`);
    if (recent.removed > 0) parts.push(`${recent.removed} removed`);
    tone = "green";
    label = `↻ ${total} ${total === 1 ? "file" : "files"}`;
    title = `Drive sync ${relativeTime(recent.last_synced_at)}: ${parts.join(", ")}. Click to open the files tab.`;
    to = `/projects/${encodeURIComponent(projectId)}/files`;
  }

  return (
    <Link
      to={to}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: compact ? "1px 6px" : "2px 8px",
        borderRadius: 10,
        fontSize: compact ? 10 : 11,
        textDecoration: "none",
        whiteSpace: "nowrap",
        ...TONE_STYLE[tone],
      }}
    >
      {label}
    </Link>
  );
};
